import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const slugify = (s) =>
  (s || "").toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

export default function CreateOrg() {
  const navigate = useNavigate();
  const { org, refreshOrg } = useAuth();

  // One org per account (2026-07-13): already in one -> this page isn't for you.
  useEffect(() => {
    if (org) navigate(`/orgs/${org.slug}`, { replace: true });
  }, [org, navigate]);

  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugEdited, setSlugEdited] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const onNameChange = (v) => {
    setName(v);
    if (!slugEdited) setSlug(slugify(v));
  };

  const submit = async () => {
    if (!name.trim()) {
      toast.error("Give your org a name");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/orgs", { name: name.trim(), slug: slug.trim() || undefined });
      toast.success(`${data.name} is live`);
      refreshOrg(); // the nav pill and publish lockout key off this
      navigate(`/orgs/${data.slug}/setup`);
    } catch (e) {
      toast.error(apiErr(e, "Could not create org"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AppShell>
      <div className="mx-auto max-w-lg">
        <div className="flex items-center gap-2">
          <Building2 className="h-5 w-5 text-[color:var(--rb-accent)]" />
          <h1 className="rb-display text-2xl font-semibold tracking-tight text-[color:var(--rb-text)]">Start a team registry</h1>
        </div>
        <p className="mt-1 text-sm text-[color:var(--rb-text-faint)]">
          A private, governed catalog for your team — free for up to 5 seats, no card required.
        </p>

        <div className="mt-6 space-y-4 rounded-2xl border border-[color:var(--rb-border)] bg-[color:var(--rb-obsidian)] p-5">
          <div>
            <label className="text-xs text-[color:var(--rb-text-faint)]">Org name</label>
            <Input
              value={name}
              onChange={(e) => onNameChange(e.target.value)}
              placeholder="Acme Engineering"
              data-testid="create-org-name-input"
              className="mt-1 border-[color:var(--rb-border)] bg-[color:var(--rb-code)] text-[color:var(--rb-text)]"
            />
          </div>
          <div>
            <label className="text-xs text-[color:var(--rb-text-faint)]">Slug</label>
            <div className="mt-1 flex items-center gap-2">
              <span className="rb-mono text-xs text-[color:var(--rb-text-faint)]">/orgs/</span>
              <Input
                value={slug}
                onChange={(e) => {
                  setSlugEdited(true);
                  setSlug(slugify(e.target.value));
                }}
                placeholder="acme"
                data-testid="create-org-slug-input"
                className="rb-mono border-[color:var(--rb-border)] bg-[color:var(--rb-code)] text-sm text-[color:var(--rb-text)]"
              />
            </div>
          </div>
          <Button
            onClick={submit}
            disabled={submitting || !name.trim()}
            data-testid="create-org-submit-button"
            className="h-11 w-full rounded-xl bg-[color:var(--rb-accent)] text-[color:var(--rb-void)] hover:bg-[color:var(--rb-accent-2)] disabled:opacity-50"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create org"}
          </Button>
        </div>
      </div>
    </AppShell>
  );
}
